// Integrytas Group - Common Security Module (CSM)
// Wraps a form field and applies personalization: hidden / read-only / required.
// In personalization mode, shows inline toggles instead of the field itself.

import { useEffect, type ReactNode } from 'react';
import { Eye, EyeOff, Lock, LockOpen, Asterisk, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePersonalization } from '../../lib/use_personalization';
import { registerFlnField } from '../../lib/fln_fields';

interface PersonalizedFieldProps {
  componentKey: string;
  fieldKey:     string;
  label:        string;
  children:     ReactNode;
  /** Pass the read-only variant of the field here when readonly is forced. */
  readonlyChildren?: ReactNode;
  /** Optional backing table — declared by the page to aid field-lineage resolution. */
  sourceTable?: string;
}

export function PersonalizedField({
  componentKey, fieldKey, label, children, readonlyChildren, sourceTable,
}: PersonalizedFieldProps) {
  const { t } = useTranslation();
  const {
    isPersonalizing, fieldPref, setFieldPref, designPanelKey,
    isInspecting, inspectField, inspectSelection, pageCode,
  } = usePersonalization();
  // When a FieldRenderer design panel is active it is the single editor for its
  // fields: read prefs from the panel's component key and render read-only (no
  // duplicate inline editor). The inline editor only drives standalone fields.
  const panelManaged = designPanelKey !== null;
  const effectiveKey = panelManaged ? designPanelKey : componentKey;
  const pref = fieldPref(effectiveKey, fieldKey);

  // Self-register for field lineage (FLN): a field is "covered" the moment it renders.
  useEffect(() => {
    registerFlnField({ page_code: pageCode, component_key: componentKey, field_key: fieldKey, label, source_table: sourceTable });
  }, [pageCode, componentKey, fieldKey, label, sourceTable]);

  // Inspect mode: the field (prompt + control) becomes a click target that reveals
  // its data source. Interaction with the control itself is suppressed.
  if (isInspecting) {
    if (!pref.visible) return null;
    const selected = inspectSelection?.component_key === componentKey && inspectSelection?.field_key === fieldKey;
    return (
      <div
        onClick={() => inspectField(componentKey, fieldKey, label)}
        title={t('fln.field.inspect', 'Show where this data comes from')}
        className={'relative cursor-help rounded ' +
          (selected ? 'ring-2 ring-primary' : 'ring-1 ring-transparent hover:ring-primary/40')}
      >
        <div className="pointer-events-none">
          {pref.readonly && readonlyChildren ? readonlyChildren : children}
        </div>
        <span className="pointer-events-none absolute right-1 top-1 text-primary/70"><Info size={11} /></span>
      </div>
    );
  }

  if (isPersonalizing && !panelManaged) {
    return (
      <div className="flex items-center gap-1 rounded border border-dashed border-primary/40 bg-primary/5 px-2 py-1">
        <span className="min-w-0 flex-1 truncate text-csm-sm text-csm-ink">{label}</span>
        <button
          type="button"
          title={pref.visible ? 'Hide field' : 'Show field'}
          onClick={() => setFieldPref(componentKey, fieldKey, { visible: !pref.visible })}
          className={'h-6 w-6 rounded border flex items-center justify-center ' +
            (pref.visible ? 'border-csm-border bg-white text-csm-muted hover:text-primary' : 'border-primary bg-primary text-white')}
        >
          {pref.visible ? <Eye size={11} /> : <EyeOff size={11} />}
        </button>
        <button
          type="button"
          title={pref.readonly ? 'Allow editing' : 'Make read-only'}
          onClick={() => setFieldPref(componentKey, fieldKey, { readonly: !pref.readonly })}
          className={'h-6 w-6 rounded border flex items-center justify-center ' +
            (pref.readonly ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-muted hover:text-primary')}
        >
          {pref.readonly ? <Lock size={11} /> : <LockOpen size={11} />}
        </button>
        <button
          type="button"
          title={pref.required ? 'Make optional' : 'Make required'}
          onClick={() => setFieldPref(componentKey, fieldKey, { required: !pref.required })}
          className={'h-6 w-6 rounded border flex items-center justify-center ' +
            (pref.required ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-muted hover:text-primary')}
        >
          <Asterisk size={11} />
        </button>
      </div>
    );
  }

  if (!pref.visible) return null;

  return (
    <div className={pref.required ? 'csm-required-field' : undefined}>
      {pref.readonly && readonlyChildren ? readonlyChildren : children}
    </div>
  );
}
